import React from 'react';
import {
  Box, Container, Grid, Typography, Link, Divider, IconButton, Stack,
} from '@mui/material';
import {
  LocalHospital, Phone, Email, LocationOn, Warning, Facebook,
  Twitter, YouTube, AccessTime,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { emergencyNumbers, hospitalInfo } from '../../data/hospitalData';

const Footer: React.FC = () => {
  const { t, language } = useLanguage();

  const quickLinks = [
    { label: t.nav.home, path: '/' },
    { label: t.nav.about, path: '/about' },
    { label: t.nav.departments, path: '/departments' },
    { label: t.nav.doctors, path: '/doctors' },
    { label: t.nav.appointment, path: '/appointment' },
    { label: t.nav.patients, path: '/patients' },
    { label: t.nav.news, path: '/news' },
    { label: t.nav.contact, path: '/contact' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#0B1929',
        color: 'grey.300',
        pt: 7,
        pb: 3,
        mt: 'auto',
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={5}>
          {/* Brand */}
          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  borderRadius: 2,
                  p: 0.75,
                  display: 'inline-flex',
                }}
              >
                <LocalHospital sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={800} color="white" lineHeight={1.1}>
                  Hôpital Régional
                </Typography>
                <Typography variant="caption" sx={{ letterSpacing: '0.08em', color: 'grey.400', fontSize: '0.7rem' }}>
                  DE BAFOUSSAM
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: 'grey.400', lineHeight: 1.8, mb: 2, maxWidth: 300 }}>
              {t.footer.description}
            </Typography>
            {/* Social */}
            <Stack direction="row" spacing={1}>
              {[Facebook, Twitter, YouTube].map((Icon, i) => (
                <IconButton
                  key={i}
                  size="small"
                  href="#"
                  sx={{
                    color: 'grey.400',
                    border: '1px solid',
                    borderColor: 'grey.700',
                    '&:hover': { color: 'primary.light', borderColor: 'primary.light', bgcolor: 'transparent' },
                  }}
                >
                  <Icon sx={{ fontSize: 18 }} />
                </IconButton>
              ))}
            </Stack>
          </Grid>

          {/* Quick Links */}
          <Grid item>
            <Typography variant="subtitle2" fontWeight={700} color="white" mb={2} sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.75rem' }}>
              {t.footer.quickLinks}
            </Typography>
            <Stack spacing={1}>
              {quickLinks.map((link) => (
                <Link
                  key={link.path}
                  component={RouterLink}
                  to={link.path}
                  underline="none"
                  sx={{
                    color: 'grey.400',
                    fontSize: '0.875rem',
                    '&:hover': { color: 'primary.light' },
                    transition: 'color 0.2s',
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Contact */}
          <Grid item>
            <Typography variant="subtitle2" fontWeight={700} color="white" mb={2} sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.75rem' }}>
              {t.footer.contact}
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <LocationOn sx={{ fontSize: 16, color: 'primary.light', mt: 0.2, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: 'grey.400', lineHeight: 1.6 }}>
                  {language === 'en' ? hospitalInfo.addressEn : hospitalInfo.addressFr}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Phone sx={{ fontSize: 16, color: 'primary.light', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: 'grey.400' }}>
                  {emergencyNumbers.main}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Email sx={{ fontSize: 16, color: 'primary.light', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: 'grey.400' }}>
                  {hospitalInfo.email}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Hours + Emergency */}
          <Grid item>
            <Typography variant="subtitle2" fontWeight={700} color="white" mb={2} sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.75rem' }}>
              {t.footer.hours}
            </Typography>
            <Stack spacing={0.75} mb={3}>
              {[t.footer.mondayFriday, t.footer.saturday, t.footer.sunday].map((line) => (
                <Box key={line} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <AccessTime sx={{ fontSize: 14, color: 'grey.500', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: 'grey.400', fontSize: '0.8rem' }}>
                    {line}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Box
              sx={{
                bgcolor: '#D32F2F22',
                border: '1px solid #D32F2F55',
                borderRadius: 2,
                p: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Warning sx={{ fontSize: 16, color: '#FF5252' }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#FF5252', fontSize: '0.78rem' }}>
                  {t.footer.emergency}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'grey.300', fontSize: '0.8rem' }}>
                {t.footer.emergencyAlways}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'grey.800', my: 4 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="body2" sx={{ color: 'grey.600', fontSize: '0.78rem' }}>
            © {language === 'en'
              ? `Copyright ${new Date().getFullYear()} WinorGroup. All rights reserved.`
              : `Copyright ${new Date().getFullYear()} WinorGroup. Tous droits réservés.`}
          </Typography>
          <Typography variant="body2" sx={{ color: 'grey.600', fontSize: '0.78rem' }}>
            Bafoussam, Région de l'Ouest, Cameroun 🇨🇲
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
